/**
 * Admin UI extras for marketplace.experiment.startAt (Wave 1.5 cohort gate).
 */

import React from "react";
import { Alert, Form, Input, Space, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import PricingFieldHelpBubble from "./PricingFieldHelpBubble.jsx";
import {
  EXPERIMENT_START_AT_KEY,
  getExperimentCohortStatus,
} from "../utils/experimentCohort.js";
import { getMarketplaceFieldHelp } from "../utils/marketplaceSettingsHelp.js";

const { Text } = Typography;

/**
 * @param {object} props
 * @param {object} props.setting
 */
const MarketplaceExperimentStartAtField = ({ setting }) => {
  const watchedStartAt = Form.useWatch(EXPERIMENT_START_AT_KEY);
  const currentValue =
    watchedStartAt == null ? "" : String(watchedStartAt).trim();
  const cohort = getExperimentCohortStatus({
    [EXPERIMENT_START_AT_KEY]: currentValue,
  });
  const startAtHelp = getMarketplaceFieldHelp(EXPERIMENT_START_AT_KEY);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type={cohort.open ? "success" : "warning"}
        showIcon
        message={
          cohort.open
            ? "ProviderOpportunity auto-create is active for this cohort"
            : "ProviderOpportunity auto-create is blocked until startAt is set"
        }
        description={
          <>
            After deposit verification, the backend creates a ProviderOpportunity
            only when the experiment cohort is open. Set an ISO UTC timestamp here
            (for example <Text code>2026-08-08T12:00:00.000Z</Text>) and save.
            Leave empty to keep the cohort closed — useful for dev/staging until
            ops deliberately opens the experiment.
          </>
        }
      />

      <Space wrap align="center">
        <Tag color={cohort.color}>{cohort.label}</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Current value:{" "}
          {currentValue ? (
            <Text code>{currentValue}</Text>
          ) : (
            <Text type="danger">(empty — cohort closed)</Text>
          )}
        </Text>
      </Space>

      <Form.Item
        name={EXPERIMENT_START_AT_KEY}
        label={
          <Space size="small" align="center">
            <span>Experiment cohort start (ISO)</span>
            {startAtHelp && <PricingFieldHelpBubble help={startAtHelp} />}
          </Space>
        }
        extra={
          setting?.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {setting.description}
            </Text>
          ) : null
        }
      >
        <Input
          placeholder="2026-08-08T12:00:00.000Z"
          aria-label="Experiment cohort start ISO timestamp"
        />
      </Form.Item>

      {!cohort.open ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Orders with verified deposits will not receive a ProviderOpportunity until
          this value is saved. See{" "}
          <Link to="/settings">System Settings → Features</Link>.
        </Text>
      ) : null}
    </Space>
  );
};

export default MarketplaceExperimentStartAtField;
